import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly maxVisibleToasts = 4;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly toasts = signal<Toast[]>([]);

  show({ message, type, duration = 4500 }: Omit<Toast, 'id'>): void {
    const id = crypto.randomUUID();
    this.toasts.update((current) => {
      const next = [...current, { id, message, type, duration }];
      const overflow = Math.max(next.length - this.maxVisibleToasts, 0);
      const removed = overflow > 0 ? next.slice(0, overflow) : [];
      removed.forEach((toast) => this.clearTimer(toast.id));
      return next.slice(overflow);
    });

    if (duration > 0) {
      this.timers.set(id, setTimeout(() => this.dismiss(id), duration));
    }
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this.toasts.update((current) => current.filter((t) => t.id !== id));
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (!timer) return;
    clearTimeout(timer);
    this.timers.delete(id);
  }
}
